import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Modal, Space, Table, Tag } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DownOutlined } from '@ant-design/icons/lib';
import { Link } from 'react-router-dom';
import {
    deleteCampaign,
    terminateCampaign,
} from '../../../../redux/actions/backoffice/promotions-actions';
import {
    canManagePromotionCampaign,
    canViewThirdPartyCampaignFeatures,
    getCampaignOwnerDisplayName,
    getCampaignOwnershipTagColor,
    getTerminateCampaignPopupText,
} from '../../promotionUtils';

const { confirm } = Modal;

const getItems = (
    record: any,
    canUserManageCampaigns: boolean,
    requestPromotions: Function,
    dispatch: any,
    t: any,
) => {
    const actions = [];

    const canManageCurrentCampaign = canUserManageCampaigns && canManagePromotionCampaign(record);

    if (canManageCurrentCampaign && record.status === 'UPCOMING') {
        actions.push({
            key: 'edit',
            label: <Link to={`/casino/awarded-promotion/${record.id}/edit`}>Edit</Link>,
        });
    }

    const showConfirm = (title: string, content: any, onOk: any) => {
        confirm({
            title,
            icon: <ExclamationCircleOutlined />,
            content,
            okText: t('yes'),
            okType: 'danger',
            onOk,
            onCancel() {},
        });
    };

    if (canManageCurrentCampaign && record.status === 'UPCOMING') {
        actions.push({
            key: 'remove',
            label: t('remove_campaign'),
            onClick: () =>
                showConfirm(t('do_you_want_to_remove_this_campaign'), null, () => {
                    dispatch(deleteCampaign(record.id));
                    setTimeout(() => {
                        requestPromotions();
                    }, 1000);
                }),
        });
    }

    if (canManageCurrentCampaign && record.status === 'ONGOING') {
        actions.push({
            key: 'terminate',
            label: t('terminate_campaign'),
            onClick: () =>
                showConfirm(
                    t('do_you_want_to_terminate_this_campaign'),
                    getTerminateCampaignPopupText(t),
                    () => {
                        dispatch(terminateCampaign({ id: record.id, force: true }));
                        setTimeout(() => {
                            requestPromotions();
                        }, 1000);
                    },
                ),
        });
    }

    if (canManageCurrentCampaign) {
        actions.push({
            key: 'clone',
            label: <Link to={`/casino/awarded-promotion/clone/${record.id}`}>Clone</Link>,
        });
    }

    return actions;
};

const PromotionsColumns = (
    canUserManageCampaigns: boolean,
    requestPromotions: Function,
    dispatch: any,
) => {
    const { t } = useTranslation();
    const canViewThirdPartyFeatures = canViewThirdPartyCampaignFeatures();

    return [
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name',
            width: 165,
        },
        ...(canViewThirdPartyFeatures
            ? [
                  {
                      title: 'Owner',
                      dataIndex: 'owner',
                      key: 'owner',
                      width: 130,
                      render: (_: any, record: any) => (
                          <Tag color={getCampaignOwnershipTagColor(record)}>
                              {getCampaignOwnerDisplayName(record)}
                          </Tag>
                      ),
                  },
              ]
            : []),
        {
            title: t('startAt'),
            dataIndex: 'startDate',
            key: 'startDate',
            sorter: true,
            width: 165,
        },
        {
            title: t('endAt'),
            dataIndex: 'endDate',
            key: 'endDate',
            sorter: true,
            width: 165,
        },
        {
            title: t('timezone'),
            dataIndex: 'timezone',
            key: 'timezone',
            width: 165,
        },
        {
            title: t('currencyCode'),
            dataIndex: 'currencyCode',
            key: 'currencyCode',
            width: 105,
        },
        Table.EXPAND_COLUMN,
        {
            title: t('games'),
            dataIndex: 'gamesDisplay',
            key: 'games',
            width: 185,
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            width: 165,
            render: (text: any, record: any) => {
                const colorToStatus: any = {
                    DISABLED: 'red',
                    ONGOING: 'green',
                    UPCOMING: 'blue',
                    ENDED: 'gold',
                };

                return (
                    <Tag color={colorToStatus[record.status]}>{t(record.status.toLowerCase())}</Tag>
                );
            },
        },
        {
            title: t('actions'),
            key: 'action',
            fixed: 'right',
            width: 135,
            render: (_: any, record: any) => (
                <Space size="large">
                    {(() => {
                        const canManageCurrentCampaign =
                            canUserManageCampaigns && canManagePromotionCampaign(record);

                        return (
                            <>
                    <Link
                        className="text-red-600 hover:text-red-300"
                        to={`/casino/awarded-promotion/${record.id}`}
                    >
                        View
                    </Link>
                    {record.status === 'ENDED' && canManageCurrentCampaign ? (
                        <Link
                            className="text-red-600 hover:text-red-300"
                            to={`/casino/awarded-promotion/clone/${record.id}`}
                        >
                            Clone
                        </Link>
                    ) : null}
                    {record.status !== 'ENDED' && canManageCurrentCampaign ? (
                        <Dropdown
                            menu={{
                                items: getItems(
                                    record,
                                    canUserManageCampaigns,
                                    requestPromotions,
                                    dispatch,
                                    t,
                                ),
                            }}
                        >
                            <a>
                                More <DownOutlined />
                            </a>
                        </Dropdown>
                    ) : null}
                            </>
                        );
                    })()}
                </Space>
            ),
        },
    ];
};

export default PromotionsColumns;
